import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Banknote, Landmark, TrendingUp, FileText, Trash2, ArrowLeft, Plus } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const chip = (label, tone) => <span className={`chip chip-${tone} text-xs`}>{label}</span>;
const mecTone = (s) => s === "PASS" ? "green" : s === "MEC BREACH" ? "amber" : "gray";

// ==================== POLICY BANK (Infinite Banking) ====================
export function PolicyBankPage() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null); // policy id being viewed
  const [showNew, setShowNew] = useState(false);
  const [f, setF] = useState({ Carrier: "", Insured: "", Owner: "", DeathBenefit: "", BasePremium: "", PUAPremium: "", DividendRate: 5.5, SevenPayAnnual: "", IssueDate: "", InsuredAge: "", NonDirectRecognition: true, Status: "In force" });
  const reload = useCallback(async () => { const r = await A.ibc.list(); setRows(r.data); }, []);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!f.Carrier || !f.Insured) return toast.error("Carrier and insured are required");
    const payload = { ...f };
    ["DeathBenefit", "BasePremium", "PUAPremium", "DividendRate", "SevenPayAnnual", "InsuredAge"].forEach(k => payload[k] = payload[k] === "" ? null : Number(payload[k]));
    await A.ibc.create(payload);
    setShowNew(false);
    setF({ Carrier: "", Insured: "", Owner: "", DeathBenefit: "", BasePremium: "", PUAPremium: "", DividendRate: 5.5, SevenPayAnnual: "", IssueDate: "", InsuredAge: "", NonDirectRecognition: true, Status: "In force" });
    toast.success("Policy added to the bank"); reload();
  };

  if (open) return <PolicyDetail pid={open} onBack={() => { setOpen(null); reload(); }} />;
  if (!rows) return <div>Loading…</div>;

  const totCV = rows.reduce((s, r) => s + (r.CashValue || 0), 0);
  const totDB = rows.reduce((s, r) => s + (r.DeathBenefit || 0), 0);
  const totAvail = rows.reduce((s, r) => s + (r.available_to_borrow || 0), 0);

  return (
    <div data-testid="policy-bank-page">
      <H1>Policy Bank</H1>
      <Sub>The Grove's private banking system — participating whole-life policies operated under the Infinite Banking Concept. Cash value, MEC status and loan capacity, all in one ledger. Illustrative figures; the carrier illustration is controlling.</Sub>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card><div className="label-caps">Policies</div><div className="font-display text-4xl mt-2" style={{ color: 'var(--primary)' }}>{rows.length}</div></Card>
        <Card><div className="label-caps">Total cash value</div><div className="font-display text-3xl mt-2" style={{ color: 'var(--primary)' }}>{fmt(totCV)}</div></Card>
        <Card><div className="label-caps">Available to borrow</div><div className="font-display text-3xl mt-2" style={{ color: 'var(--gold-hover)' }}>{fmt(totAvail)}</div></Card>
        <Card><div className="label-caps">Death benefit</div><div className="font-display text-3xl mt-2" style={{ color: 'var(--primary)' }}>{fmt(totDB)}</div></Card>
      </div>

      <div className="banner-warning mb-4 text-xs">MEC guardrail: keep cumulative premiums within the carrier's 7-pay limit. Projections here are illustrative — always confirm against the carrier's in-force illustration. Nothing here is tax or legal advice.</div>

      <div className="flex justify-between items-center mb-3">
        <H2>Policies</H2>
        <button className="btn-gold flex items-center gap-1" onClick={() => setShowNew(!showNew)} data-testid="new-policy-btn"><Plus size={15} /> New policy</button>
      </div>

      {showNew && (
        <Card className="mb-4" data-testid="new-policy-form">
          <div className="grid md:grid-cols-3 gap-3">
            <input placeholder="Carrier (e.g. MassMutual)" value={f.Carrier} onChange={e => setF({ ...f, Carrier: e.target.value })} data-testid="policy-carrier" />
            <input placeholder="Insured" value={f.Insured} onChange={e => setF({ ...f, Insured: e.target.value })} data-testid="policy-insured" />
            <input placeholder="Owner (trust/entity)" value={f.Owner} onChange={e => setF({ ...f, Owner: e.target.value })} />
            <input type="number" placeholder="Death benefit" value={f.DeathBenefit} onChange={e => setF({ ...f, DeathBenefit: e.target.value })} data-testid="policy-db" />
            <input type="number" placeholder="Base premium / yr" value={f.BasePremium} onChange={e => setF({ ...f, BasePremium: e.target.value })} data-testid="policy-base" />
            <input type="number" placeholder="PUA premium / yr" value={f.PUAPremium} onChange={e => setF({ ...f, PUAPremium: e.target.value })} data-testid="policy-pua" />
            <input type="number" step="0.1" placeholder="Dividend rate %" value={f.DividendRate} onChange={e => setF({ ...f, DividendRate: e.target.value })} />
            <input type="number" placeholder="7-pay premium (carrier)" value={f.SevenPayAnnual} onChange={e => setF({ ...f, SevenPayAnnual: e.target.value })} data-testid="policy-7pay" />
            <input type="number" placeholder="Insured age" value={f.InsuredAge} onChange={e => setF({ ...f, InsuredAge: e.target.value })} />
            <div><label className="text-xs" style={{ color: 'var(--muted)' }}>Issue date</label><input type="date" value={f.IssueDate} onChange={e => setF({ ...f, IssueDate: e.target.value })} /></div>
            <select value={f.Status} onChange={e => setF({ ...f, Status: e.target.value })}>{["In force", "Applied", "Lapse risk", "Surrendered"].map(o => <option key={o}>{o}</option>)}</select>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-auto" checked={f.NonDirectRecognition} onChange={e => setF({ ...f, NonDirectRecognition: e.target.checked })} /> Non-direct recognition</label>
          </div>
          <button className="btn-primary mt-3" onClick={create} data-testid="save-policy-btn">Add to Policy Bank</button>
        </Card>
      )}

      {rows.length === 0 ? (
        <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No policies yet. Add your first participating whole-life policy to begin banking.</div></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map(p => (
            <Card key={p.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setOpen(p.id)} data-testid={`policy-card-${p.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-display text-lg" style={{ color: 'var(--primary)' }}>{p.Insured}</div>
                  <div className="text-xs" style={{ color: 'var(--muted)' }}>{p.Carrier} · {p.Owner || "—"}</div>
                </div>
                {chip(p.mec_status || "UNKNOWN", mecTone(p.mec_status))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div><div className="label-caps">Cash value</div><div className="font-mono text-lg" style={{ color: 'var(--primary)' }}>{fmt(p.CashValue)}</div></div>
                <div><div className="label-caps">Can borrow</div><div className="font-mono text-lg" style={{ color: 'var(--gold-hover)' }}>{fmt(p.available_to_borrow)}</div></div>
                <div><div className="label-caps">Loan bal.</div><div className="font-mono text-lg">{fmt(p.total_loan_balance)}</div></div>
              </div>
              <div className="text-xs mt-3" style={{ color: 'var(--muted)' }}>DB {fmt(p.DeathBenefit)} · base {fmt(p.BasePremium)} + PUA {fmt(p.PUAPremium)}/yr · {p.Status}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyDetail({ pid, onBack }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [proj, setProj] = useState(null);
  const [led, setLed] = useState({ date: new Date().toISOString().slice(0, 10), kind: "premium", amount: "", note: "" });
  const [loan, setLoan] = useState({ principal: "", rate: 5, note_date: new Date().toISOString().slice(0, 10), purpose: "", borrower: "" });
  const [drafting, setDrafting] = useState(false);

  const load = useCallback(async () => {
    const [r, pr] = await Promise.all([A.ibc.get(pid), A.ibc.projection(pid, 30)]);
    setD(r.data); setProj(pr.data);
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div>Loading…</div>;
  const p = d.policy;

  const addLedger = async () => {
    if (!led.amount) return toast.error("Amount required");
    await A.ibc.addLedger(pid, { ...led, amount: Number(led.amount) });
    setLed({ ...led, amount: "", note: "" }); toast.success("Ledger entry recorded"); load();
  };
  const addLoan = async () => {
    if (!loan.principal) return toast.error("Principal required");
    await A.ibc.addLoan(pid, { ...loan, principal: Number(loan.principal), rate: Number(loan.rate) });
    setLoan({ ...loan, principal: "", purpose: "" }); toast.success("Policy loan drawn"); load();
  };
  const repay = async (lid) => {
    const amt = prompt("Repayment amount ($):"); if (!amt) return;
    await A.ibc.repay(lid, { date: new Date().toISOString().slice(0, 10), amount: Number(amt) });
    toast.success("Repayment recorded"); load();
  };
  const makeDraft = async (template, extra = {}) => {
    setDrafting(true);
    try {
      const r = await A.ibc.draft(pid, { template, ...extra });
      toast.success(`Drafted: ${r.data.title}`);
      nav(`/studio/${r.data.draft_id}`);
    } catch (e) { toast.error("Draft failed"); } finally { setDrafting(false); }
  };

  const mec = d.mec;
  return (
    <div data-testid="policy-detail-page">
      <button className="btn-outline flex items-center gap-1 mb-4" onClick={onBack} data-testid="policy-back-btn"><ArrowLeft size={14} /> Back to Policy Bank</button>
      <H1>{p.Insured}</H1>
      <Sub>{p.Carrier} · owned by {p.Owner || "—"} · {p.Type}</Sub>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card><div className="label-caps">Cash value</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{fmt(p.CashValue)}</div></Card>
        <Card><div className="label-caps">Available to borrow</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--gold-hover)' }}>{fmt(d.available_to_borrow)}</div></Card>
        <Card><div className="label-caps">Loan balance</div><div className="font-display text-3xl mt-1">{fmt(d.total_loan_balance)}</div></Card>
        <Card><div className="label-caps">MEC 7-pay</div><div className="mt-2">{chip(mec.status, mecTone(mec.status))}</div></Card>
      </div>

      {/* MEC monitor */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-2"><TrendingUp size={16} style={{ color: 'var(--primary)' }} /><div className="font-display text-lg">MEC 7-pay monitor</div></div>
        <div className="text-xs mb-3" style={{ color: 'var(--muted)' }}>{mec.note}</div>
        {mec.years?.length ? (
          <table><thead><tr><th>Policy year</th><th>Cumulative paid</th><th>7-pay limit</th><th>Status</th></tr></thead>
            <tbody>{mec.years.map(y => (<tr key={y.policy_year}><td>Year {y.policy_year}</td><td className="font-mono">{fmt(y.cumulative_paid)}</td><td className="font-mono">{fmt(y.cumulative_limit)}</td><td>{y.ok ? chip("within limit", "green") : chip("OVER LIMIT", "amber")}</td></tr>))}</tbody>
          </table>
        ) : <div className="text-sm" style={{ color: 'var(--muted)' }}>Set the issue date and carrier 7-pay premium on the policy, then log premiums to monitor MEC.</div>}
      </Card>

      {/* Projection chart */}
      <Card className="mb-6">
        <div className="font-display text-lg mb-1">Illustrative cash-value projection (30 yr)</div>
        <div className="text-xs mb-3" style={{ color: 'var(--muted)' }}>Assumed dividend {proj?.dividend_rate}%. 7-pay {proj?.seven_pay_carrier ? `(carrier ${fmt(proj.seven_pay_carrier)})` : `(estimate ${fmt(proj?.seven_pay_estimate?.annual_estimate)} — ${proj?.seven_pay_estimate?.note})`}</div>
        {proj?.rows?.length && (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={proj.rows} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="cash_value" stroke="#1B3B2B" strokeWidth={2} dot={false} name="Cash value" />
              <Line type="monotone" dataKey="cumulative_paid" stroke="#C9A050" strokeWidth={2} dot={false} name="Paid in" />
              <Line type="monotone" dataKey="death_benefit" stroke="#8A8270" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Death benefit" />
            </LineChart>
          </ResponsiveContainer>
        )}
        <div className="flex gap-4 text-xs mt-2" style={{ color: 'var(--muted)' }}>
          <span>■ <span style={{ color: '#1B3B2B' }}>Cash value</span></span>
          <span>■ <span style={{ color: '#C9A050' }}>Cumulative paid</span></span>
          <span>┅ Death benefit</span>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Ledger */}
        <Card>
          <H2>Cash-value ledger</H2>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input type="date" value={led.date} onChange={e => setLed({ ...led, date: e.target.value })} />
            <select value={led.kind} onChange={e => setLed({ ...led, kind: e.target.value })} data-testid="ledger-kind">{["premium", "pua", "dividend", "interest", "adjustment"].map(o => <option key={o} value={o}>{o}</option>)}</select>
            <input type="number" placeholder="Amount" value={led.amount} onChange={e => setLed({ ...led, amount: e.target.value })} data-testid="ledger-amount" />
            <input placeholder="Note" value={led.note} onChange={e => setLed({ ...led, note: e.target.value })} />
          </div>
          <button className="btn-primary" onClick={addLedger} data-testid="add-ledger-btn">Record entry</button>
          <div className="mt-3 max-h-56 overflow-y-auto">
            <table><thead><tr><th>Date</th><th>Kind</th><th>Amount</th></tr></thead>
              <tbody>{(d.ledger || []).map(e => (<tr key={e.id}><td className="font-mono text-xs">{e.date}</td><td className="text-xs">{e.kind}</td><td className="font-mono">{fmt(e.amount)}</td></tr>))}</tbody></table>
          </div>
        </Card>

        {/* Loans */}
        <Card>
          <H2>Policy loans</H2>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input type="number" placeholder="Principal" value={loan.principal} onChange={e => setLoan({ ...loan, principal: e.target.value })} data-testid="loan-principal" />
            <input type="number" step="0.1" placeholder="Rate %" value={loan.rate} onChange={e => setLoan({ ...loan, rate: e.target.value })} />
            <input type="date" value={loan.note_date} onChange={e => setLoan({ ...loan, note_date: e.target.value })} />
            <input placeholder="Borrower" value={loan.borrower} onChange={e => setLoan({ ...loan, borrower: e.target.value })} />
            <input placeholder="Purpose" value={loan.purpose} onChange={e => setLoan({ ...loan, purpose: e.target.value })} className="col-span-2" />
          </div>
          <button className="btn-primary" onClick={addLoan} data-testid="add-loan-btn">Draw policy loan</button>
          <div className="mt-3 max-h-56 overflow-y-auto">
            {(d.loans || []).map(l => (
              <div key={l.id} className="p-2 mb-2 rounded" style={{ background: 'var(--surface-2)' }}>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">{fmt(l.principal)} @ {l.rate_pct}%</div>
                  <div>{l.status === "Repaid" ? chip("repaid", "green") : chip(`bal ${fmt(l.balance)}`, "gray")}</div>
                </div>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>{l.purpose || "—"} · accrued {fmt(l.accrued_interest)}</div>
                {l.status !== "Repaid" && <button className="btn-outline text-xs mt-1" onClick={() => repay(l.id)} data-testid={`repay-${l.id}`}>Record repayment</button>}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Drafting */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-2"><FileText size={16} style={{ color: 'var(--primary)' }} /><div className="font-display text-lg">Draft documents</div></div>
        <div className="text-xs mb-3" style={{ color: 'var(--muted)' }}>Generate a ready-to-edit draft into the Instrument Studio (export to .docx / .pdf there). All drafts are marked DRAFT FOR REVIEW.</div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary" disabled={drafting} onClick={() => makeDraft("banking_resolution")} data-testid="draft-banking-resolution">Banking resolution</button>
          <button className="btn-outline" disabled={drafting} onClick={() => makeDraft("application_checklist")} data-testid="draft-app-checklist">Application checklist</button>
          <button className="btn-outline" disabled={drafting} onClick={() => makeDraft("funding_schedule", { years: 15 })} data-testid="draft-funding-schedule">Funding schedule</button>
          <button className="btn-outline" disabled={drafting} onClick={() => { const a = prompt("Loan amount ($):"); if (a) makeDraft("promissory_note", { amount: Number(a), borrower: p.Owner }); }} data-testid="draft-promissory">Promissory note</button>
          <button className="btn-outline" disabled={drafting} onClick={() => { const a = prompt("Loan amount ($):"); if (a) makeDraft("policy_loan_agreement", { amount: Number(a) }); }} data-testid="draft-loan-agreement">Policy loan agreement</button>
        </div>
      </Card>

      <button className="btn-outline text-xs flex items-center gap-1" style={{ color: 'var(--overdue)' }} onClick={async () => { if (window.confirm("Delete this policy and its ledger/loans?")) { await A.ibc.del(pid); onBack(); } }} data-testid="delete-policy-btn"><Trash2 size={13} /> Delete policy</button>
    </div>
  );
}

// ==================== FUNDING RADAR ====================
export function FundingRadarPage() {
  const [q, setQ] = useState("family trust");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (query = q) => { setBusy(true); try { const r = await A.fundingRadar(query); setD(r.data); } finally { setBusy(false); } };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div data-testid="funding-radar-page">
      <H1>Funding Radar</H1>
      <Sub>Live, aggregated capital signals matched to the Grove — open grants, SBA-guaranteed loans, actual federal awards, and the current AFR benchmark for pricing loans.</Sub>
      <div className="flex gap-2 mb-4"><input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} className="max-w-lg" data-testid="radar-q" placeholder="keyword (e.g. family trust, housing, veteran)" /><button className="btn-primary" onClick={() => load()} disabled={busy} data-testid="radar-search">Scan</button></div>

      {!d ? <div>Scanning…</div> : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card><div className="label-caps">Internal sources</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{d.internal.total_sources}</div><div className="text-xs" style={{ color: 'var(--muted)' }}>{d.internal.no_pg} No-PG · {d.internal.verified} verified</div></Card>
          <Card><div className="label-caps">AFR mid-term</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--gold-hover)' }}>{d.benchmark.afr_mid?.toFixed(3)}%</div><div className="text-xs" style={{ color: 'var(--muted)' }}>price notes at ≥ AFR</div></Card>
          <Card><div className="label-caps">Open grants</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{d.open_grants.length}</div></Card>
          <Card><div className="label-caps">SBA loans (live)</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{d.sba_awards.length}</div></Card>
        </div>

        <H2>Open grant opportunities (Grants.gov)</H2>
        <Card className="p-0 overflow-hidden mb-6"><table><thead><tr><th>Title</th><th>Agency</th><th>Closes</th><th></th></tr></thead>
          <tbody>{d.open_grants.map(g => (<tr key={g.id}><td>{g.title}</td><td className="text-xs">{g.agency}</td><td className="font-mono text-xs">{g.closeDate}</td><td><a className="btn-outline text-xs" href={g.url} target="_blank" rel="noreferrer">Open</a></td></tr>))}</tbody></table></Card>

        <H2>SBA-guaranteed loans (USAspending)</H2>
        <Card className="p-0 overflow-hidden mb-6"><table><thead><tr><th>Recipient</th><th>Loan value</th><th>Sub-agency</th><th>Issued</th></tr></thead>
          <tbody>{d.sba_awards.map((s, i) => (<tr key={i}><td className="font-medium">{s.recipient}</td><td className="font-mono text-xs">{fmt(s.amount)}</td><td className="text-xs">{s.sub_agency}</td><td className="font-mono text-xs">{s.start}</td></tr>))}</tbody></table></Card>

        <H2>Recent federal awards</H2>
        <Card className="p-0 overflow-hidden"><table><thead><tr><th>Recipient</th><th>Amount</th><th>Agency</th><th>Purpose</th></tr></thead>
          <tbody>{d.federal_awards.map((a, i) => (<tr key={i}><td className="font-medium">{a.recipient}</td><td className="font-mono text-xs">{fmt(a.amount)}</td><td className="text-xs">{a.agency}</td><td className="text-xs">{a.description}</td></tr>))}</tbody></table></Card>
      </>)}
    </div>
  );
}

// ==================== MARKET / ECONOMIC DATA ====================
const WB = [["FP.CPI.TOTL.ZG", "Inflation (CPI %)"], ["NY.GDP.MKTP.CD", "GDP (US$)"], ["FR.INR.RINR", "Real interest rate %"], ["SL.UEM.TOTL.ZS", "Unemployment %"]];
const BLS = [["CUUR0000SA0", "CPI-U index"], ["LNS14000000", "Unemployment rate %"], ["CES0000000001", "Nonfarm employment (000s)"]];

export function MarketDataPage() {
  const [wbInd, setWbInd] = useState(WB[0][0]);
  const [wb, setWb] = useState(null);
  const [blsId, setBlsId] = useState(BLS[0][0]);
  const [bls, setBls] = useState(null);
  const [debt, setDebt] = useState(null);
  const [fred, setFred] = useState(null);

  useEffect(() => { A.publicData.worldBank(wbInd).then(r => setWb(r.data)); }, [wbInd]);
  useEffect(() => { A.publicData.bls(blsId).then(r => setBls(r.data)); }, [blsId]);
  useEffect(() => { A.publicData.treasuryDebt().then(r => setDebt(r.data)); A.publicData.fred("DFF").then(r => setFred(r.data)); }, []);

  return (
    <div data-testid="market-data-page">
      <H1>Market & Economic Data</H1>
      <Sub>Official, keyless-first data feeds — World Bank, U.S. Treasury, and the Bureau of Labor Statistics — to frame the Grove's capital decisions.</Sub>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-2"><div className="font-display text-lg flex items-center gap-2"><Landmark size={16} /> World Bank</div>
            <select value={wbInd} onChange={e => setWbInd(e.target.value)} className="max-w-[220px]" data-testid="wb-indicator">{WB.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          {wb?.series?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={[...wb.series].reverse()} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" /><XAxis dataKey="year" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#1B3B2B" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm" style={{ color: 'var(--muted)' }}>No data.</div>}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-2"><div className="font-display text-lg flex items-center gap-2"><TrendingUp size={16} /> Bureau of Labor Statistics</div>
            <select value={blsId} onChange={e => setBlsId(e.target.value)} className="max-w-[220px]" data-testid="bls-series">{BLS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          {bls?.rate_limited && <div className="banner-warning text-xs mb-2">{bls.note}</div>}
          {bls?.data?.length ? (
            <table><thead><tr><th>Period</th><th>Value</th></tr></thead><tbody>{bls.data.slice(0, 8).map((r, i) => (<tr key={i}><td className="text-xs">{r.period} {r.year}</td><td className="font-mono">{r.value}</td></tr>))}</tbody></table>
          ) : !bls?.rate_limited && <div className="text-sm" style={{ color: 'var(--muted)' }}>No data.</div>}
        </Card>

        <Card>
          <div className="font-display text-lg flex items-center gap-2 mb-2"><Banknote size={16} /> U.S. National Debt (Debt to the Penny)</div>
          {debt?.length ? (<>
            <div className="font-display text-3xl mb-2" style={{ color: 'var(--primary)' }}>{fmt(debt[0].total)}</div>
            <div className="text-xs mb-3" style={{ color: 'var(--muted)' }}>as of {debt[0].date} · public {fmt(debt[0].public)} · intragov {fmt(debt[0].intragov)}</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={[...debt].reverse()} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" /><XAxis dataKey="date" tick={{ fontSize: 9 }} hide /><YAxis tick={{ fontSize: 9 }} tickFormatter={v => `$${(v / 1e12).toFixed(0)}T`} /><Tooltip formatter={v => fmt(v)} /><Line type="monotone" dataKey="total" stroke="#C9A050" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </>) : <div className="text-sm" style={{ color: 'var(--muted)' }}>Loading…</div>}
        </Card>

        <Card>
          <div className="font-display text-lg flex items-center gap-2 mb-2"><TrendingUp size={16} /> Federal Reserve (FRED)</div>
          {fred?.requires_key ? (
            <div className="banner-warning text-xs">{fred.note}</div>
          ) : fred?.observations?.length ? (
            <table><thead><tr><th>Date</th><th>Fed funds rate</th></tr></thead><tbody>{fred.observations.slice(0, 10).map((o, i) => (<tr key={i}><td className="font-mono text-xs">{o.date}</td><td className="font-mono">{o.value}</td></tr>))}</tbody></table>
          ) : <div className="text-sm" style={{ color: 'var(--muted)' }}>No data.</div>}
        </Card>
      </div>
    </div>
  );
}
